import React from 'react';
import './LegalPages.css';

type LegalPageProps = {
  title: string;
  updatedOn: string;
  children: React.ReactNode;
  showProductsNote?: boolean;
};

const LegalPageLayout: React.FC<LegalPageProps> = ({
  title,
  updatedOn,
  children,
  showProductsNote = true
}) => (
  <div className="legal-page">
    <div className="legal-main">
      <div className="container legal-container">
        <h1>{title}</h1>
        <p className="legal-updated">Last updated: {updatedOn}</p>

        {showProductsNote && (
          <section className="legal-note">
            <h2>Our products</h2>
            <p>
              Xenvota owns, operates, and continuously supports two products:{' '}
              <a href="https://emailvalidify.com/">Email Validify</a> and{' '}
              <a href="http://enhanzio.xenvota.com/">Enhanzio</a>. Email Validify
              focuses on email verification, list quality, and deliverability performance. Enhanzio is
              our AI-powered image enhancement platform for restoring and sharpening photos with premium
              output quality.
            </p>
          </section>
        )}

        {children}
      </div>
    </div>
  </div>
);

export const PrivacyPolicyPage: React.FC = () => (
  <LegalPageLayout title="Privacy policy" updatedOn="30 Mar, 2026" showProductsNote={false}>
    <section>
      <p>
        Welcome to <a href="/"><strong>Xenvota Tech Solutions</strong></a> (&quot;Company&quot;, &quot;we&quot;,
        &quot;our&quot;, or &quot;us&quot;). We respect your privacy and are committed to protecting the personal information you share with us when using our website. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website. We operate the website <a href="/"><strong>www.xenvota.com</strong> </a> and related products including <a href='https://emailvalidify.com/'>Email Validify</a> and <a href="http://enhanzio.xenvota.com/">Enhanzio</a>.
      </p>
    </section>
    <section>
      <h3>1.1 Personal Information</h3>
      <p>We may collect personal data that you voluntarily provide, including:</p>
      <ul>
        <li>Full name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Company name (if applicable)</li>
        <li>Any information submitted via contact forms, signup forms, or support requests</li>
      </ul>
    </section>

    <section>
      <h3>1.2 Account &amp; Service Data</h3>
      <p>If you use our products (such as Enhanzio), we may collect:</p>
      <ul>
        <li>Login details (if account system exists)</li>
        <li>Uploaded images or content for processing</li>
        <li>Usage activity related to our services</li>
      </ul>
    </section>

    <section>
      <h3>1.3 Automatically Collected Data</h3>
      <p>When you visit our website, we may automatically collect:</p>
      <ul>
        <li>IP address</li>
        <li>Browser type and version</li>
        <li>Device type and operating system</li>
        <li>Pages visited and time spent</li>
        <li>Referring URLs</li>
      </ul>
    </section>

    <section>
      <h3>1.4 Cookies &amp; Tracking Technologies</h3>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Enhance user experience</li>
        <li>Analyze website traffic</li>
        <li>Store user preferences</li>
      </ul>
      <p>You can manage or disable cookies through your browser settings.</p>
    </section>

    <section>
      <h2>2. How We Use Your Information</h2>
      <p>We use collected data for the following purposes:</p>
      <ul>
        <li>To provide and operate our services</li>
        <li>To process user requests and support inquiries</li>
        <li>To improve website performance and user experience</li>
        <li>To communicate updates, offers, or service-related notices</li>
        <li>To ensure security, prevent fraud, and detect misuse</li>
        <li>To comply with legal and regulatory requirements</li>
      </ul>
    </section>

    <section>
      <h2>3. Processing of Uploaded Content (Important for Enhanzio)</h2>
      <p>If you upload images or media to our platform:</p>
      <ul>
        <li>Files are processed solely to deliver the requested service</li>
        <li>We do <strong>not claim ownership</strong> of your content</li>
        <li>We do <strong>not use your content for training AI models</strong> unless explicitly stated</li>
        <li>Files may be temporarily stored for processing and deleted after a reasonable period</li>
      </ul>
    </section>

    <section>
      <h2>4. Sharing and Disclosure of Information</h2>
      <p>We do <strong>not sell, rent, or trade your personal information</strong>.</p>
      <h4>We may share information with:</h4>
      <ul>
        <li>
          Trusted third-party service providers
          <ul>
            <li>Hosting providers</li>
            <li>Analytics tools</li>
            <li>Email service providers</li>
          </ul>
        </li>
        <li>Payment processors such as Razorpay for transaction processing</li>
        <li>Legal authorities when required by applicable law</li>
      </ul>
      <p>All third parties are obligated to protect your data.</p>
    </section>

    <section>
      <h2>5. Data Security</h2>
      <p>
        We implement appropriate technical and organizational measures to protect your data,
        including:
      </p>
      <ul>
        <li>Secure servers</li>
        <li>Access controls</li>
        <li>Data encryption where applicable</li>
      </ul>
      <p>However, no method of transmission over the internet is 100% secure.</p>
    </section>

    <section>
      <h2>6. Data Retention</h2>
      <p>We retain personal data only for as long as necessary:</p>
      <ul>
        <li>To fulfill the purposes outlined in this policy</li>
        <li>To comply with legal obligations</li>
        <li>To resolve disputes and enforce agreements</li>
      </ul>
    </section>

    <section>
      <h2>7. Your Rights</h2>
      <h4>Depending on your jurisdiction, you may have the right to:</h4>
      <ul>
        <li>Access your personal data</li>
        <li>Request correction of inaccurate information</li>
        <li>Request deletion of your data</li>
        <li>Withdraw consent at any time</li>
      </ul>
      <p>
        To exercise your rights, contact us at:{' '}
        <a href="mailto:support@xenvota.com">support@xenvota.com</a>
      </p>
    </section>

    <section>
      <h2>8. Third-Party Services &amp; Links</h2>
      <p>
        Our website may contain links to third-party websites or services. We are not responsible
        for the privacy practices of those external sites.
      </p>
    </section>

    <section>
      <h2>9. International Data Transfers</h2>
      <p>
        Your information may be processed and stored on servers located outside your country. By
        using our services, you consent to such transfers.
      </p>
    </section>

    <section>
      <h2>10. Children&apos;s Privacy</h2>
      <p>
        Our services are not intended for individuals under the age of 18. We do not knowingly
        collect personal data from children.
      </p>
    </section>

    <section>
      <h2>11. Updates to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Any changes will be posted on this
        page with a revised &quot;Last Updated&quot; date.
      </p>
    </section>

    <section>
      <h2>12. Contact Information</h2>
      <p>If you have any questions or concerns regarding this Privacy Policy, please contact:</p>
      <p>
      Email: <a href="mailto:support@xenvota.com">support@xenvota.com</a><br />
      Phone: <a href="tel:+919588288100">+91 9588288100</a><br />
      </p>
    </section>

    <section>
      <p>
        By using our website and services, you agree to the terms outlined in this Privacy Policy.
      </p>
    </section>
  </LegalPageLayout>
);

export const TermsPage: React.FC = () => (
  <LegalPageLayout title="Terms & Conditions" updatedOn="30 Mar, 2026" showProductsNote={false}>
    <section>
      <p>
      Welcome to <a href="/"><strong>Xenvota Tech Solutions</strong></a> (&quot;Company&quot;, &quot;we&quot;,
        &quot;our&quot;, or &quot;us&quot;). These Terms &amp; Conditions govern your use of our
        website <a href="/"><strong>www.xenvota.com</strong> </a> and related products including <a href='https://emailvalidify.com/'>Email Validify</a> and <a href="http://enhanzio.xenvota.com/">Enhanzio</a>.
      </p>
      <p>
        By accessing or using our website and services, you agree to be bound by these Terms.
      </p>
    </section>

    <section>
      <h2>1. Use of Services</h2>
      <p>You agree to use our services only for lawful purposes and in accordance with these Terms.</p>
      <p>You must not:</p>
      <ul>
        <li>Use the service for any illegal or unauthorized purpose</li>
        <li>Attempt to gain unauthorized access to our systems</li>
        <li>Disrupt or interfere with the security or functionality of the platform</li>
      </ul>
    </section>

    <section>
      <h2>2. Services Overview</h2>
      <p>Xenvota Tech Solutions provides:</p>
      <ul>
        <li>AI-powered tools (such as AI photo enhancement via Enhanzio)</li>
        <li>Software solutions and digital services</li>
      </ul>
      <p>
        We reserve the right to modify, suspend, or discontinue any service at any time without
        prior notice.
      </p>
    </section>

    <section>
      <h2>3. User Accounts (if applicable)</h2>
      <p>If you create an account:</p>
      <ul>
        <li>You are responsible for maintaining confidentiality of your credentials</li>
        <li>You agree to provide accurate information</li>
        <li>You are responsible for all activities under your account</li>
      </ul>
      <p>We reserve the right to suspend or terminate accounts for violations.</p>
    </section>

    <section>
      <h2>4. User Content</h2>
      <p>When you upload content (e.g., images):</p>
      <ul>
        <li>You retain ownership of your content</li>
        <li>You grant us a limited license to process the content for providing services</li>
        <li>You confirm that your content does not violate any laws or third-party rights</li>
      </ul>
      <p>We do not claim ownership of your content.</p>
    </section>

    <section>
      <h2>5. Payments &amp; Billing</h2>
      <p>Certain services may be paid.</p>
      <ul>
        <li>Payments are processed securely via third-party providers such as Razorpay</li>
        <li>You agree to provide accurate payment details</li>
        <li>All fees are displayed clearly before purchase</li>
      </ul>
      <p>We reserve the right to change pricing at any time.</p>
    </section>

    <section>
      <h2>6. Refund &amp; Cancellation</h2>
      <p>
        Refunds and cancellations are governed by our <strong>Refund Policy</strong>. Please review
        it separately on our website.
      </p>
    </section>

    <section>
      <h2>7. Intellectual Property</h2>
      <p>All content on this website, including:</p>
      <ul>
        <li>Text</li>
        <li>Graphics</li>
        <li>Logos</li>
        <li>Software</li>
      </ul>
      <p>
        is the property of Xenvota Tech Solutions or its licensors and is protected by applicable
        laws.
      </p>
      <p>You may not copy, reproduce, or distribute any content without permission.</p>
    </section>

    <section>
      <h2>8. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law:</p>
      <ul>
        <li>We are not liable for any indirect, incidental, or consequential damages</li>
        <li>We do not guarantee uninterrupted or error-free service</li>
        <li>Use of our services is at your own risk</li>
      </ul>
    </section>

    <section>
      <h2>9. Disclaimer</h2>
      <p>Our services are provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind.</p>
      <p>We do not guarantee:</p>
      <ul>
        <li>Accuracy of results</li>
        <li>Continuous availability</li>
        <li>Error-free performance</li>
      </ul>
    </section>

    <section>
      <h2>10. Termination</h2>
      <p>We reserve the right to suspend or terminate your access to services if:</p>
      <ul>
        <li>You violate these Terms</li>
        <li>You misuse the platform</li>
      </ul>
    </section>

    <section>
      <h2>11. Third-Party Services</h2>
      <p>
        We may integrate or use third-party services. We are not responsible for their content,
        policies, or practices.
      </p>
    </section>

    <section>
      <h2>12. Governing Law</h2>
      <p>These Terms shall be governed by and interpreted in accordance with the laws of India.</p>
      <p>Any disputes shall be subject to the jurisdiction of courts located in Delhi, India.</p>
    </section>

    <section>
      <h2>13. Changes to Terms</h2>
      <p>
        We may update these Terms at any time. Continued use of the website after changes
        constitutes acceptance of updated Terms.
      </p>
    </section>

    <section>
      <h2>14. Contact Information</h2>
      <p>If you have any questions regarding these Terms, please contact:</p>
      <p>
      Email: <a href="mailto:support@xenvota.com">support@xenvota.com</a><br />
      Phone: <a href="tel:+919588288100">+91 9588288100</a><br />
      </p>
    </section>

    <section>
      <p>
        By using our services, you acknowledge that you have read, understood, and agreed to these
        Terms &amp; Conditions.
      </p>
    </section>
  </LegalPageLayout>
);

export const RefundPolicyPage: React.FC = () => (
  <LegalPageLayout title="Refund Policy" updatedOn="30 Mar, 2026">
    <section>
      <p>
        Thank you for choosing our services. This Refund Policy outlines the terms under which
        refunds may be issued for purchases made through our platform.
      </p>
    </section>

    <section>
      <h2>1. No Refund Policy</h2>
      <p>
        Refund eligibility is limited and is assessed according to this policy. In general, once a
        subscription or service is purchased, refunds are not available for:
      </p>
      <ul>
        <li>Change of mind</li>
        <li>Partial usage of the service</li>
        <li>Failure to cancel before the renewal date</li>
        <li>Unused credits or features</li>
      </ul>
    </section>

    <section>
      <h2>2. Exceptions</h2>
      <p>We may consider refunds only in the following cases:</p>
      <ul>
        <li>
          <strong>Duplicate payment:</strong> If you were charged more than once for the same
          service
        </li>
        <li>
          <strong>Technical issues:</strong> If a critical issue on our platform prevents you from
          using the service, and our support team is unable to resolve it within a reasonable time
        </li>
      </ul>
      <p>All refund requests are reviewed on a case-by-case basis.</p>
    </section>

    <section>
      <h2>3. Subscription Cancellation</h2>
      <p>You can cancel your subscription at any time. However:</p>
      <ul>
        <li>Cancellation will stop future billing</li>
        <li>
          You will continue to have access to the service until the end of your current billing
          cycle
        </li>
        <li>No refunds will be issued for the remaining period after cancellation</li>
      </ul>
    </section>

    <section>
      <h2>4. Requesting a Refund</h2>
      <p>To request a refund, please contact us at:</p>
      <p>
        <strong>Email:</strong> <a href="mailto:support@xenvota.com">support@xenvota.com</a>
      </p>
      <p>Include the following details:</p>
      <ul>
        <li>Your account email</li>
        <li>Transaction ID or payment receipt</li>
        <li>Reason for the refund request</li>
      </ul>
    </section>

    <section>
      <h2>5. Processing of Refunds</h2>
      <p>If your refund request is approved:</p>
      <ul>
        <li>Refunds will be processed to the original payment method</li>
        <li>
          It may take <strong>5-10 business days</strong> for the amount to reflect in your
          account, depending on your payment provider
        </li>
      </ul>
    </section>

    <section>
      <h2>6. Changes to This Policy</h2>
      <p>
        We reserve the right to update or modify this Refund Policy at any time. Changes will be
        effective immediately upon posting on this page.
      </p>
    </section>

    <section>
      <h2>7. Contact Us</h2>
      <p>If you have any questions about this Refund Policy, please contact us at:</p>
      <p>
      Email: <a href="mailto:support@xenvota.com">support@xenvota.com</a><br />
      Phone: <a href="tel:+919588288100">+91 9588288100</a><br />
      </p>
    </section>
  </LegalPageLayout>
);

export const ShippingPolicyPage: React.FC = () => (
  <LegalPageLayout title="Shipping Policy" updatedOn="30 March 2026">
    <section>
      <h2>Delivery Type</h2>
      <p>
        Xenvota provides digital services and software solutions. No physical products are shipped
        through this website.
      </p>
    </section>
    <section>
      <h2>Digital Delivery</h2>
      <p>
        Project updates, files, and deliverables are shared via email, secure cloud links, or
        client-approved collaboration platforms.
      </p>
    </section>
    <section>
      <h2>Delivery Timelines</h2>
      <p>
        Timelines vary based on scope and are communicated in proposals, milestone plans, or
        project agreements.
      </p>
    </section>
  </LegalPageLayout>
);
